/**
 * features/finance/Journals.tsx
 * ------------------------------------------------------------------
 * The journal register, and the one place a human posts to the ledger.
 *
 * Everything else that reaches the ledger — an invoice, a receipt, a credit
 * note, a payslip — posts from its own source document through a trigger.
 * What lands here is what has no source document: an opening balance, a
 * correction, a reclassification between accounts, a month-end accrual.
 *
 * THREE THINGS THIS SCREEN REFUSES TO DO, all deliberate:
 *
 *   It never EDITS a posted journal. A mistake is corrected by a reversal —
 *   a contra with its own number, pointing at what it undoes. A set of books
 *   where an entry can be quietly rewritten is a set nobody can sign, and
 *   the Firestore rules enforce this independently of anything here.
 *
 *   It never DELETES one, for the same reason.
 *
 *   It never posts an UNBALANCED journal. The Post button stays disabled
 *   until debits equal credits to the thebe, and buildJournal throws anyway
 *   if something slipped past — a journal that does not balance is not a
 *   display problem, it is a ledger that cannot be trusted at all.
 *
 * The running difference is shown while typing, because "out of balance by
 * 40.00" is the single most useful thing to tell someone entering a journal,
 * and working it out in your head is how the wrong figure gets forced in.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import {
  BookOpen, Plus, Trash2, Loader2, X, AlertCircle, CheckCircle2,
  Undo2, Lock, LockOpen, ChevronDown, ChevronRight,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getPermissions } from '@/lib/permissions';
import { PageHeader, SoftCard, StatChip, glassFieldClass, btnPrimary, btnGhost } from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';
import { useGlData } from '@/features/finance/useGlData';
import {
  postJournal, postReversal, fetchClosedPeriods, closePeriod, reopenPeriod,
  newManualSourceId,
} from '@/features/finance/glWrite';
import {
  totalsFor, validateLines, journalDateProblem, periodIdFor,
  type GlJournal, type JournalLine,
} from '@/lib/gl';

const todayIso = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface DraftLine {
  accountCode: string;
  dr: string;
  cr: string;
  narration: string;
}
const emptyLine = (): DraftLine => ({ accountCode: '', dr: '', cr: '', narration: '' });

export default function Journals() {
  const { userData } = useAuth();
  const perms = getPermissions(userData?.roles ?? userData?.role);
  const canPost = perms.canPostJournals;

  const [fy, setFy] = useState(`FY${new Date().getFullYear()}`);
  const gl = useGlData(fy);

  const [closed, setClosed] = useState<Set<string>>(new Set());
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [reversing, setReversing] = useState<GlJournal | null>(null);
  const [reverseReason, setReverseReason] = useState('');
  const [showPeriods, setShowPeriods] = useState(false);

  // the draft
  const [composing, setComposing] = useState(false);
  const [date, setDate] = useState(todayIso());
  const [narration, setNarration] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([emptyLine(), emptyLine()]);

  useEffect(() => { void fetchClosedPeriods().then(setClosed).catch(() => setClosed(new Set())); }, []);

  const actor = useMemo(
    () => ({ uid: userData?.uid, name: userData?.displayName, email: userData?.email }),
    [userData],
  );

  const postable = useMemo(
    () => gl.accounts.filter((a) => a.isPostable && a.active),
    [gl.accounts],
  );

  /** The draft as the engine sees it — blank rows dropped. */
  const draftLines = useMemo<JournalLine[]>(
    () =>
      lines
        .filter((l) => l.accountCode || l.dr || l.cr)
        .map((l) => ({
          accountCode: l.accountCode,
          dr: Number(l.dr) || 0,
          cr: Number(l.cr) || 0,
          ...(l.narration ? { narration: l.narration } : {}),
        })),
    [lines],
  );

  const totals = useMemo(() => totalsFor(draftLines), [draftLines]);
  const difference = Math.round((totals.totalDr - totals.totalCr) * 100) / 100;
  const dateProblem = journalDateProblem(date, closed);
  const lineProblems = draftLines.length ? validateLines(draftLines) : ['Add at least one line.'];
  const ready =
    canPost && !busy && !dateProblem && lineProblems.length === 0 && narration.trim().length >= 3;

  function setLine(i: number, patch: Partial<DraftLine>) {
    setLines((cur) => cur.map((l, n) => (n === i ? { ...l, ...patch } : l)));
  }

  function resetDraft() {
    setDate(todayIso());
    setNarration('');
    setLines([emptyLine(), emptyLine()]);
  }

  async function post() {
    if (!ready) return;
    setBusy(true);
    setMsg(null);
    try {
      const j = await postJournal({
        type: 'manual',
        sourceId: newManualSourceId(),
        date,
        narration: narration.trim(),
        lines: draftLines,
        actor,
      });
      setMsg({ ok: true, text: `Posted ${j.journalNo} — ${money(j.totalDr)} each side.` });
      resetDraft();
      setComposing(false);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function doReverse() {
    if (!reversing) return;
    setBusy(true);
    try {
      const r = await postReversal(reversing, reverseReason, actor);
      setMsg({ ok: true, text: `Reversed by ${r.journalNo}.` });
      setReversing(null);
      setReverseReason('');
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  async function togglePeriod(monthId: string) {
    setBusy(true);
    try {
      if (closed.has(monthId)) {
        await reopenPeriod(monthId);
        setMsg({ ok: true, text: `${monthId} reopened. Anything posted into it now changes figures already reported.` });
      } else {
        await closePeriod(monthId, fy, actor);
        setMsg({ ok: true, text: `${monthId} closed. No further postings can be dated into it.` });
      }
      setClosed(await fetchClosedPeriods());
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  /** The months of this financial year, for the lock panel. */
  const months = useMemo(() => {
    const year = fy.replace(/^FY/, '');
    return Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, '0')}`);
  }, [fy]);

  const accountLabel = (code: string) => {
    const a = gl.byCode.get(code);
    return a ? `${a.code} · ${a.name}` : code;
  };

  return (
    <div className="space-y-5">
      <PageHeader icon={BookOpen} title="Journals" subtitle="The ledger register — and the only place a human posts to it" />

      <div className="flex items-center gap-3 flex-wrap">
        <select value={fy} onChange={(e) => setFy(e.target.value)} className={glassFieldClass('w-auto')}>
          {[0, 1, 2].map((back) => {
            const y = new Date().getFullYear() - back;
            return <option key={y} value={`FY${y}`}>{`FY${y}`}</option>;
          })}
        </select>
        <StatChip tone="blue" value={String(gl.journals.length)} label="Journals this year" />
        <StatChip tone="gold" value={String(closed.size)} label="Closed months" />
        <div className="ml-auto flex gap-2">
          <button onClick={() => setShowPeriods((v) => !v)} className={btnGhost}>
            <Lock size={14} /> Periods
          </button>
          {canPost && (
            <button onClick={() => { setComposing((v) => !v); setMsg(null); }} className={btnPrimary}>
              <Plus size={14} /> {composing ? 'Close' : 'New journal'}
            </button>
          )}
        </div>
      </div>

      {!canPost && (
        <SoftCard>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            You can read the register but not post to it. Posting is limited to those who may
            write journals.
          </p>
        </SoftCard>
      )}

      {msg && (
        <div className={`rounded-xl px-4 py-3 text-sm flex items-start gap-2 ${
          msg.ok
            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
        }`}>
          {msg.ok ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <AlertCircle size={16} className="mt-0.5 shrink-0" />}
          <span>{msg.text}</span>
        </div>
      )}

      {/* ---- period locks ---- */}
      {showPeriods && (
        <SoftCard>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Accounting periods</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-3">
            Closing a month stops anything being dated into it. Do it once the month has been
            reported on — after that, a backdated journal changes a figure somebody has already acted on.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {months.map((m) => {
              const isClosed = closed.has(m);
              return (
                <button
                  key={m}
                  disabled={!perms.canManageChartOfAccounts || busy}
                  onClick={() => togglePeriod(m)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors disabled:opacity-50 ${
                    isClosed
                      ? 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800/60 dark:bg-amber-900/20 dark:text-amber-200'
                      : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                  }`}
                >
                  {isClosed ? <Lock size={13} /> : <LockOpen size={13} className="text-gray-400" />}
                  {m}
                </button>
              );
            })}
          </div>
        </SoftCard>
      )}

      {/* ---- compose ---- */}
      {composing && canPost && (
        <SoftCard>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={glassFieldClass()} />
              <p className="text-[11px] mt-1 text-gray-400">Period {periodIdFor(date) || '—'}</p>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                Narration <span className="text-gray-400">— what this journal is for</span>
              </label>
              <input
                value={narration}
                onChange={(e) => setNarration(e.target.value)}
                placeholder="e.g. Reclassify August bank charges to 5570"
                className={glassFieldClass()}
              />
            </div>
          </div>

          {dateProblem && (
            <p className="mt-2 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2">
              {dateProblem}
            </p>
          )}

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-gray-500 dark:text-gray-400">
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2 font-medium">Account</th>
                  <th className="py-2 font-medium w-28 text-right">Debit</th>
                  <th className="py-2 font-medium w-28 text-right">Credit</th>
                  <th className="py-2 font-medium">Line narration</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-1.5 pr-2">
                      <select
                        value={l.accountCode}
                        onChange={(e) => setLine(i, { accountCode: e.target.value })}
                        className={glassFieldClass('text-sm')}
                      >
                        <option value="">Choose an account…</option>
                        {postable.map((a) => (
                          <option key={a.code} value={a.code}>{a.code} · {a.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1.5 px-1">
                      <input
                        type="number" step="0.01" min="0" value={l.dr}
                        // One side or the other, never both: a line that is
                        // its own debit and credit hides its net effect.
                        onChange={(e) => setLine(i, { dr: e.target.value, cr: e.target.value ? '' : l.cr })}
                        className={glassFieldClass('text-right text-sm')}
                      />
                    </td>
                    <td className="py-1.5 px-1">
                      <input
                        type="number" step="0.01" min="0" value={l.cr}
                        onChange={(e) => setLine(i, { cr: e.target.value, dr: e.target.value ? '' : l.dr })}
                        className={glassFieldClass('text-right text-sm')}
                      />
                    </td>
                    <td className="py-1.5 pl-2">
                      <input
                        value={l.narration}
                        onChange={(e) => setLine(i, { narration: e.target.value })}
                        className={glassFieldClass('text-sm')}
                      />
                    </td>
                    <td className="py-1.5 text-right">
                      {lines.length > 2 && (
                        <button onClick={() => setLines((c) => c.filter((_, n) => n !== i))} className="text-gray-300 hover:text-red-600">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="font-semibold">
                  <td className="py-2 text-right pr-2 text-gray-500">Totals</td>
                  <td className="py-2 text-right">{money(totals.totalDr)}</td>
                  <td className="py-2 text-right">{money(totals.totalCr)}</td>
                  <td colSpan={2} className="py-2 pl-3">
                    {/* The single most useful thing to show while typing. */}
                    {difference === 0 ? (
                      <span className="text-emerald-600 dark:text-emerald-400 text-xs">Balanced</span>
                    ) : (
                      <span className="text-red-600 dark:text-red-400 text-xs">
                        Out of balance by {money(Math.abs(difference))}
                        {difference > 0 ? ' — credits short' : ' — debits short'}
                      </span>
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <button onClick={() => setLines((c) => [...c, emptyLine()])} className={btnGhost}>
              <Plus size={13} /> Add line
            </button>
            <button onClick={post} disabled={!ready} className={btnPrimary}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <BookOpen size={14} />} Post journal
            </button>
            {lineProblems.length > 0 && draftLines.length > 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">{lineProblems[0]}</span>
            )}
          </div>
        </SoftCard>
      )}

      {/* ---- the register ---- */}
      {gl.loading ? (
        <SoftCard><div className="flex justify-center py-8"><Spinner label="Loading the ledger…" /></div></SoftCard>
      ) : gl.journals.length === 0 ? (
        <SoftCard>
          <p className="text-sm text-gray-500 dark:text-gray-400 py-2 text-center">
            No journals posted in {fy} yet.
          </p>
        </SoftCard>
      ) : (
        <SoftCard pad="p-0">
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {gl.journals.map((j) => {
              const open = expanded === j.id;
              return (
                <div key={j.id}>
                  <button
                    onClick={() => setExpanded(open ? null : j.id)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/40"
                  >
                    {open ? <ChevronDown size={15} className="text-gray-400 shrink-0" /> : <ChevronRight size={15} className="text-gray-400 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {j.journalNo}
                        <span className="text-gray-400 font-normal"> · {j.date}</span>
                        {j.reverses && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700">reversal</span>}
                        {j.reversedBy && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">reversed</span>}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{j.narration}</p>
                    </div>
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 shrink-0">
                      {money(j.totalDr)}
                    </span>
                  </button>

                  {open && (
                    <div className="px-4 pb-3">
                      <table className="w-full text-xs">
                        <tbody>
                          {j.lines.map((l, i) => (
                            <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                              <td className="py-1.5 text-gray-700 dark:text-gray-200">{accountLabel(l.accountCode)}</td>
                              <td className="py-1.5 text-gray-500">{l.narration ?? ''}</td>
                              <td className="py-1.5 text-right w-24">{l.dr ? money(l.dr) : ''}</td>
                              <td className="py-1.5 text-right w-24">{l.cr ? money(l.cr) : ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-[11px] text-gray-400">
                          Posted {j.postedAt?.slice(0, 10)} · source {j.source?.type}
                        </span>
                        {canPost && !j.reversedBy && !j.reverses && (
                          <button onClick={() => setReversing(j)} className="ml-auto text-xs text-red-600 hover:underline inline-flex items-center gap-1">
                            <Undo2 size={12} /> Reverse
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </SoftCard>
      )}

      {/* ---- reversal ---- */}
      {reversing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-5">
            <div className="flex items-start justify-between gap-3 mb-1">
              <h3 className="font-semibold text-gray-900 dark:text-white">Reverse {reversing.journalNo}?</h3>
              <button onClick={() => setReversing(null)} className="text-gray-400 hover:text-gray-600"><X size={16} /></button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              This posts a contra journal that undoes it. The original stays exactly as it is —
              a posted journal is never edited or removed.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              The reversal is dated <strong>today</strong>, not {reversing.date}. Reversing into a
              month that has been reported on would change a figure somebody has already acted on.
            </p>
            <input
              value={reverseReason}
              onChange={(e) => setReverseReason(e.target.value)}
              placeholder="Reason for the reversal"
              className={glassFieldClass('mt-3')}
            />
            <div className="flex gap-2 mt-4">
              <button onClick={doReverse} disabled={busy || reverseReason.trim().length < 3} className={btnPrimary}>
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />} Reverse it
              </button>
              <button onClick={() => setReversing(null)} className={btnGhost}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
